import type { DefineDateTimeFormat, I18nOptions } from 'vue-i18n';
import type { PropsDatePicker } from '@sysarb/shared-style';

import { availableLanguages } from '@sysarb/sysarb-types/general/locales';

export type DateFormatKey = 'monthAndDayAndYear' | 'monthAndDay' | 'year' | 'short' | 'long' | 'fullDate' | 'time';

const dateFormatDefault: DefineDateTimeFormat = {
    monthAndDayAndYear: {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    },
    monthAndDay: {
        month: 'short',
        day: 'numeric'
    },
    year: {
        year: 'numeric'
    },
    short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    },
    fullDate: {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    },
    time: {
        hour: 'numeric',
        minute: 'numeric'
    },
    long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric'
    },
    numeric: {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    },
};

const dateFormats: I18nOptions['datetimeFormats'] = {
    en: { ...dateFormatDefault },
    sv: { ...dateFormatDefault },
    nb: { ...dateFormatDefault },
};

export function getDateFormats(): I18nOptions['datetimeFormats'] {
    const result = { ...dateFormats };

    for (const lang of availableLanguages) {
        if (lang in result) continue;
        result[lang] = dateFormatDefault;
    }

    return result;
}

type CalendarLocale = Omit<NonNullable<PropsDatePicker['locale']>, 'id'>;

/**
 * Get the v-calendar mask of a localized date, e.g. 'DD.MM.YYYY' for nb
 * @param localeValue
 * @param options The date format to convert, e.g. the numeric format above
 */
function getDateMask(localeValue: string, options: Intl.DateTimeFormatOptions): string {
    // 1 January is used because a single-digit day and month show whether the locale pads them (01 vs 1)
    const parts = new Intl.DateTimeFormat(localeValue, options).formatToParts(new Date(2000, 0, 1));
    return parts.map((part) => {
        if (part.type === 'year') return 'YYYY';
        if (part.type === 'month') return part.value.length === 2 ? 'MM' : 'M';
        if (part.type === 'day') return part.value.length === 2 ? 'DD' : 'D';
        return `[${part.value}]`;
    }).join('');
}

/**
 * Turn a mask generated in getDateMask into a readable placeholder, e.g. 'M[/]D[/]YYYY' to 'M/D/YYYY'
 * @param mask
 */
function maskToPlaceholder(mask: string): string {
    return mask.replace(/\[([^\]]*)\]/g, '$1');
}

const datePickerLocales: Record<string, CalendarLocale> = Object.fromEntries(
    availableLanguages.map((language) => [language, { masks: { L: getDateMask(language, dateFormatDefault.numeric) } }]),
);

export const calendarLocales: Record<string, CalendarLocale> = {
    ...datePickerLocales,
};

export const datePickerPlaceholders: Record<string, string> = Object.fromEntries(
    Object.entries(calendarLocales).map(([language, { masks }]) => [language, maskToPlaceholder(masks.L)]),
);
